import { useState } from 'react';
import { Plus, Trash2, ArrowLeft, Check, Infinity as InfinityIcon, AlertCircle, HelpCircle } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createPlan, createFeature, getFeatures, deleteFeature } from '@/services/subscriptionService';
import { useTranslation } from 'react-i18next';


interface FeatureI {
  featureId: number;
  included: boolean;
}

interface CreatePlanFormData {
  name: string;
  description: string;
  priceEuro: string; // En euros (será convertido a centimos)
  icon: string;
  popular: boolean;
  limit: string;
  active: boolean;
}

const CreatePlan = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CreatePlanFormData>({
    name: '',
    description: '',
    priceEuro: '',
    icon: '',
    popular: false,
    limit: '',
    active: true,
  });

  // Features seleccionados para este plan
  const [selectedFeatures, setSelectedFeatures] = useState<FeatureI[]>([]);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureIcon, setNewFeatureIcon] = useState<'Check' | 'Infinity'>('Check');
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; featureId: number } | null>(null);

  // Query para obtener features existentes
  const { data: availableFeatures = [], isLoading: featuresLoading } = useQuery({
    queryKey: ['features'],
    queryFn: getFeatures,
  });

  // Mutation para crear feature
  const createFeatureMutation = useMutation({
    mutationFn: (data: { icon: string; name: string }) =>
      createFeature(data.icon, data.name),
    onSuccess: (newFeature) => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      setNewFeatureName('');
      setNewFeatureIcon('Check');
      toast.success(t('featureCreated'));
      // Seleccionar automáticamente el nuevo feature
      setSelectedFeatures((prev) => [...prev, { featureId: newFeature.id, included: true }]);
    },
    onError: () => {
      toast.error(t('errorCreatingFeature'));
    },
  });

  // Mutation para eliminar feature
  const deleteFeatureMutation = useMutation({
    mutationFn: (featureId: number) => deleteFeature(featureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      toast.success(t('featureDeleted'));
      setDeleteConfirm(null);
    },
    onError: () => {
      toast.error(t('cannotDeleteFeatureInUse'));
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleAddFeature = async () => {
    if (newFeatureName.trim() === '') {
      toast.error(t('pleaseEnterFeatureName'));
      return;
    }
    createFeatureMutation.mutate({ icon: newFeatureIcon, name: newFeatureName.trim() });
  };

  const handleSelectFeature = (featureId: number) => {
    setSelectedFeatures((prev) => [
      ...prev,
      { featureId, included: true }
    ]);
  };

  const handleRemoveFeature = (featureId: number) => {
    setSelectedFeatures((prev) => prev.filter((f) => f.featureId !== featureId));
  };

  const handleToggleIncluded = (featureId: number) => {
    setSelectedFeatures((prev) =>
      prev.map((f) =>
        f.featureId === featureId ? { ...f, included: !f.included } : f
      )
    );
  };

  const handleDeleteFeature = (featureId: number) => {
    deleteFeatureMutation.mutate(featureId);
  };

  const startTour = () => {
    driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: [
        {
          element: '#create-plan-title',
          popover: {
            title: t('createNewPlan'),
            description: t('tourCreatePlanDesc'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#basic-information-section',
          popover: {
            title: t('basicInformation'),
            description: t('tourBasicInfoDesc'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#plan-name-input',
          popover: {
            title: t('planName'),
            description: t('tourPlanNameDesc'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#plan-price-input',
          popover: {
            title: t('monthlyPrice'),
            description: t('tourPlanPriceDesc'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#plan-description-input',
          popover: {
            title: t('description'),
            description: t('tourPlanDescriptionDesc'),
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#plan-options-checkboxes',
          popover: {
            title: t('planOptions'),
            description: t('tourPlanOptionsDesc'),
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#features-section',
          popover: {
            title: t('planFeatures'),
            description: t('tourFeaturesDesc'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#create-feature-box',
          popover: {
            title: t('createNewFeature'),
            description: t('tourCreateFeatureDesc'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#available-features-list',
          popover: {
            title: t('availableFeatures'),
            description: t('tourAvailableFeaturesDesc'),
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#selected-features-list',
          popover: {
            title: t('selectedFeatures'),
            description: t('tourSelectedFeaturesDesc'),
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#create-plan-actions',
          popover: {
            title: t('createPlan'),
            description: t('tourCreatePlanActionsDesc'),
            side: 'top',
            align: 'start',
          },
        },
      ],
    }).drive();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!formData.name.trim()) {
      toast.error(t('planNameRequired'));
      return;
    }
    if (!formData.description.trim()) {
      toast.error(t('descriptionRequired'));
      return;
    }
    if (!formData.priceEuro) {
      toast.error(t('priceRequired'));
      return;
    }

    // Convertir precio de euros a centimos
    const priceInCents = Math.round(parseFloat(formData.priceEuro) * 100);

    const planData = {
      name: formData.name,
      description: formData.description,
      price: priceInCents,
      icon: formData.icon || null,
      features: selectedFeatures,
      popular: formData.popular,
      limit: formData.limit ? parseInt(formData.limit) : null,
      active: formData.active,
    };

    // TODO: Implementar llamada API para crear plan
    await createPlan(planData);
    toast.success(t('planCreatedSuccessfully'));
    navigate('/subscriptions-manage');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/subscriptions-manage')}
          className="mb-6 inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('back')}
        </button>

        <div className="mb-8 flex items-start justify-between" id="create-plan-title">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('createNewPlan')}</h1>
            <p className="text-gray-600">{t('completePlanSubscriptionDetails')}</p>
          </div>
          <button
            type="button"
            onClick={startTour}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm transition hover:bg-gray-50"
            title={t('startTour')}
          >
            <HelpCircle className="h-4 w-4" /> {t('help')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div id="basic-information-section" className="rounded-2xl bg-white p-8 shadow-sm">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">{t('basicInformation')}</h2>

            <div className="grid gap-6 sm:grid-cols-2">
              {/* Nombre */}
              <div id="plan-name-input">
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('planName')} *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('planNamePlaceholder')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Precio en euros */}
              <div id="plan-price-input">
                <label htmlFor="priceEuro" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('monthlyPrice')} *
                </label>
                <div className="relative">
                  <input
                    id="priceEuro"
                    name="priceEuro"
                    type="number"
                    step="0.01"
                    value={formData.priceEuro}
                    onChange={handleInputChange}
                    placeholder={t('monthlyPricePlaceholder')}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                </div>
              </div>

              {/* Icono del plan */}
              <div>
                <label htmlFor="icon" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('icon')}
                </label>
                <select
                  id="icon"
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">{t('noIcon')}</option>
                  <option value="Check">Check</option>
                  <option value="Infinity">Infinity</option>
                </select>
              </div>

              {/* Límite */}
              <div>
                <label htmlFor="limit" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('usageLimit')}
                </label>
                <input
                  id="limit"
                  name="limit"
                  type="number"
                  value={formData.limit}
                  onChange={handleInputChange}
                  placeholder={t('usageLimitPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Descripción */}
            <div id="plan-description-input" className="mt-6">
              <label htmlFor="description" className="mb-2 block text-sm font-medium text-gray-700">
                {t('description')} *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('descriptionPlaceholder')}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Checkboxes */}
            <div id="plan-options-checkboxes" className="mt-6 space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="popular"
                  checked={formData.popular}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('markAsPopularPlan')}</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('activePlan')}</span>
              </label>
            </div>
          </div>

          {/* Características */}
          <div id="features-section" className="rounded-2xl bg-white p-8 shadow-sm">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">{t('planFeatures')}</h2>

            {/* Crear nueva característica */}
            <div id="create-feature-box" className="mb-8 rounded-lg bg-blue-50 p-4 border border-blue-200">
              <h3 className="mb-4 text-sm font-semibold text-blue-900">{t('createNewFeature')}</h3>
              <div className="grid gap-3 sm:grid-cols-[1fr,200px,auto]">
                <input
                  type="text"
                  value={newFeatureName}
                  onChange={(e) => setNewFeatureName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddFeature();
                    }
                  }}
                  placeholder={t('newFeaturePlaceholder')}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <select
                  value={newFeatureIcon}
                  onChange={(e) => setNewFeatureIcon(e.target.value as 'Check' | 'Infinity')}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="Check">Check</option>
                  <option value="Infinity">Infinity</option>
                </select>
                <button
                  type="button"
                  onClick={handleAddFeature}
                  disabled={createFeatureMutation.isPending}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  {t('create')}
                </button>
              </div>
            </div>

            {/* Características disponibles */}
            <div id="available-features-list" className="mb-8">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">{t('availableFeatures')}</h3>
              {featuresLoading ? (
                <p className="text-gray-500">{t('loadingFeatures')}</p>
              ) : availableFeatures.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                  {t('noFeaturesAvailableCreate')}
                </p>
              ) : (
                <div className="space-y-2">
                  {availableFeatures
                    .filter((f) => !selectedFeatures.some((sf) => sf.featureId === f.id))
                    .map((feature) => (
                      <div key={feature.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center gap-2 rounded-md bg-white px-2 py-1 text-gray-700">
                            {feature.icon === 'Check' ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <InfinityIcon className="h-4 w-4" />
                            )}
                            {feature.icon}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{feature.name}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleSelectFeature(feature.id)}
                            className="rounded-lg bg-green-600 px-3 py-1 text-sm font-semibold text-white transition hover:bg-green-700"
                          >
                            {t('add')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm({ isOpen: true, featureId: feature.id })}
                            disabled={deleteFeatureMutation.isPending}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Características seleccionadas */}
            <div id="selected-features-list">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">{t('selectedFeatures')}</h3>
              {selectedFeatures.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                  {t('noFeaturesSelectedAdd')}
                </p>
              ) : (
                <div className="space-y-2">
                  {selectedFeatures.map((selectedFeature) => {
                    const feature = availableFeatures.find((f) => f.id === selectedFeature.featureId);
                    return (
                      <div key={selectedFeature.featureId} className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center gap-2 rounded-md bg-white px-2 py-1 text-gray-700">
                            {feature?.icon === 'Check' ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <InfinityIcon className="h-4 w-4" />
                            )}
                            {feature?.icon}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{feature?.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedFeature.included}
                              onChange={() => handleToggleIncluded(selectedFeature.featureId)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs font-medium text-gray-600">{t('included')}</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(selectedFeature.featureId)}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-100"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          <div id="create-plan-actions" className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/subscriptions-manage')}
              className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="rounded-lg bg-indigo-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              {t('createPlan')}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de confirmación para eliminar feature */}
      {deleteConfirm?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white shadow-lg">
            <div className="px-6 py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">{t('deleteFeature')}</h3>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                {t('areYouSureDeleteFeature')}
              </p>
            </div>
            <div className="flex gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => deleteConfirm && handleDeleteFeature(deleteConfirm.featureId)}
                disabled={deleteFeatureMutation.isPending}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleteFeatureMutation.isPending ? t('deleting') : t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePlan;

